import React, { useState } from 'react';
import { clsx } from 'clsx';
import { PiggyBank, Trash2 } from 'lucide-react';
import { Button } from '@components/Button';
import { ConfirmDialog, Modal } from '@components/Dialog';
import { MoneyInput } from '@components/MoneyInput';
import budgetService from '@services/budgetService';
import type { BudgetItem } from '@/types/finance';
import { competenceFromYearMonth } from '@/utils/competence';
import { formatBRL } from '@/utils/money';
import { monthLabel, type YearMonth } from '@/utils/period';
import { ColorDot, Required, labelCls } from './financeBits';

type Scope = 'default' | 'competence';

interface Props {
  item: BudgetItem;
  /** Mês da tela (a exceção vale só para ele). */
  month: YearMonth;
  onClose: () => void;
  onSaved: () => void;
  onError: (err: unknown) => void;
}

/**
 * Definir orçamento de uma categoria de despesa (PUT/DELETE /categories/:id/budget): valor em R$ e
 * alcance — "Padrão mensal" (`competence = 0`, vale para todos os meses) ou "Só em <mês>" (exceção
 * AAAAMM, vence o padrão naquele mês). Remover apaga o padrão ou a exceção, conforme o alcance.
 */
export const BudgetFormModal: React.FC<Props> = ({ item, month, onClose, onSaved, onError }) => {
  const hasDefault = (item.default_cents ?? 0) > 0;
  const hasException = (item.competence_cents ?? 0) > 0;
  const [scope, setScope] = useState<Scope>(hasException ? 'competence' : 'default');
  const [amount, setAmount] = useState<number | null>(() => (hasException ? item.competence_cents ?? null : item.default_cents ?? null));
  const [saving, setSaving] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);

  const competence = competenceFromYearMonth(month);
  const monthText = monthLabel(month);
  const existingForScope = scope === 'default' ? item.default_cents ?? null : item.competence_cents ?? null;
  const canSubmit = !saving && !removing && !!amount && amount > 0;

  const changeScope = (s: Scope) => {
    setScope(s);
    const current = s === 'default' ? item.default_cents : item.competence_cents;
    if (current) setAmount(current);
  };

  const save = async () => {
    if (!canSubmit || !amount) return;
    setSaving(true);
    try {
      await budgetService.set(item.category_id, { competence: scope === 'default' ? 0 : competence, amount_cents: amount });
      onSaved();
    } catch (err) {
      onError(err);
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    setRemoving(true);
    try {
      await budgetService.remove(item.category_id, scope === 'default' ? 0 : competence);
      setConfirmRemove(false);
      onSaved();
    } catch (err) {
      setConfirmRemove(false);
      onError(err);
    } finally {
      setRemoving(false);
    }
  };

  return (
    <Modal isOpen onClose={onClose} size="md" closeOnBackdrop={!saving && !removing}>
      <div className="px-6 py-4 border-b border-slate-200 flex items-center gap-2">
        <PiggyBank size={18} className="text-slate-500" />
        <h2 className="text-h3 inline-flex items-center gap-2 min-w-0">
          Orçamento de <ColorDot color={item.color} /> <span className="truncate">{item.name}</span>
        </h2>
      </div>
      <div className="px-6 py-4 max-h-[70vh] overflow-y-auto custom-scrollbar space-y-4">
        <div>
          <label className={labelCls}>
            Vale para
            <Required />
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" role="radiogroup">
            {(
              [
                { id: 'default', title: 'Padrão mensal', hint: 'Todos os meses, até você mudar', current: item.default_cents },
                { id: 'competence', title: `Só em ${monthText}`, hint: 'Exceção deste mês; nos demais vale o padrão', current: item.competence_cents },
              ] as { id: Scope; title: string; hint: string; current?: number | null }[]
            ).map((o) => (
              <button
                key={o.id}
                type="button"
                role="radio"
                aria-checked={scope === o.id}
                disabled={saving || removing}
                onClick={() => changeScope(o.id)}
                className={clsx('text-left rounded-lg border p-3 transition-colors', scope === o.id ? 'border-emerald-500 bg-emerald-50/60 ring-1 ring-emerald-500' : 'border-slate-200 hover:bg-slate-50')}
              >
                <p className="text-sm font-medium text-slate-800 first-letter:uppercase">{o.title}</p>
                <p className="text-small">{o.hint}</p>
                {o.current ? <p className="text-xs text-slate-600 mt-1 tabular-nums">Hoje: {formatBRL(o.current)}</p> : <p className="text-xs text-slate-400 mt-1">Não definido</p>}
              </button>
            ))}
          </div>
        </div>
        <div>
          <label className={labelCls}>
            Valor mensal
            <Required />
          </label>
          <MoneyInput value={amount} onChange={setAmount} autoFocus disabled={saving || removing} />
          <p className="text-small mt-1">
            {item.parent_category_id ? 'Subcategoria: compara só com os gastos dela.' : 'Categoria principal: compara com os gastos dela mais os das subcategorias.'}
            {' '}Aviso em 80 % e alerta no sino quando estourar.
          </p>
        </div>
        {hasDefault && hasException && (
          <p className="text-small rounded-lg bg-amber-50 border border-amber-200 p-2 text-amber-900">
            Em {monthText} vale a exceção ({formatBRL(item.competence_cents)}); o padrão ({formatBRL(item.default_cents)}) volta nos outros meses.
          </p>
        )}
      </div>
      <div className="px-6 py-4 border-t border-slate-200 flex flex-wrap items-center justify-between gap-2">
        <div>
          {existingForScope ? (
            <Button variant="outline" size="sm" icon={<Trash2 />} onClick={() => setConfirmRemove(true)} disabled={saving || removing} className="text-red-600 border-red-200 hover:bg-red-50">
              {scope === 'default' ? 'Remover padrão' : 'Remover exceção'}
            </Button>
          ) : null}
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={onClose} disabled={saving || removing}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={save} loading={saving} disabled={!canSubmit}>
            Salvar
          </Button>
        </div>
      </div>

      <ConfirmDialog
        isOpen={confirmRemove}
        onClose={() => setConfirmRemove(false)}
        onConfirm={remove}
        title={scope === 'default' ? 'Remover orçamento padrão' : 'Remover exceção do mês'}
        message={
          scope === 'default'
            ? `Remover o orçamento padrão de "${item.name}"? ${hasException ? `A exceção de ${monthText} continua valendo.` : 'A categoria fica sem orçamento (sem barra nem alerta).'}`
            : `Remover a exceção de ${monthText} de "${item.name}"? ${hasDefault ? `Volta a valer o padrão de ${formatBRL(item.default_cents)}.` : 'A categoria fica sem orçamento neste mês.'}`
        }
        confirmText="Remover"
        severity="danger"
        loading={removing}
      />
    </Modal>
  );
};

export default BudgetFormModal;
